import type { AnchorState } from "@norkron/web-docz";
import type { ThreadNode } from "./threads";
import { KIND_ORDER } from "./workbenchLayout";

/**
 * `reviewOrder` — WHERE a review note sits in the document.
 *
 * This is the thing "Document order" claimed and did not have. The rail used to
 * sort by an identity comparator over a list the server returned
 * `ORDER BY created_at`, so the label was false: notes were shown in the order
 * they were WRITTEN, never the order they are READ.
 *
 * ── the ordinal, and why it is derived HERE ────────────────────────────────
 * A note's document position is `(surface, block, offset-within-block)`:
 *
 *  · `surfaceOrd` — the workbench gutter straddles a PAIRING (two panes), so a
 *    brief-homed note and an article-homed note can both claim block 0. The
 *    pane rank leads the key, in `workbenchLayout`'s canonical left→right
 *    {@link KIND_ORDER}. A single-surface rail (`DocumentViewer`) is all rank 0.
 *  · `blockOrd` — the TOP-LEVEL block's index, exactly what `stampBlocks`
 *    numbers (`data-block-id="b{i}"`). It is a NUMBER, never the `block_ref`
 *    string: a lexical compare puts `b10` before `b2`.
 *  · `offset` — how far into that block the quote starts, so two notes on one
 *    block (and a list item / table cell, which are INSIDE their block, never a
 *    block of their own — `stampBlocks` walks `root.children` and does not
 *    recurse) keep true reading order.
 *
 * The position is NOT server-derived, and that is a decision, not a shortcut:
 * `el.comment_anchors` holds a SECTION-scoped, content-resolved anchor with no
 * block column at all, and `el.annotations.block_ref` is only the position the
 * note was WRITTEN at. Neither knows where the quote sits NOW. The read surface
 * already re-anchors every note on every paint (`resolveAnnotation` →
 * `documentPointOfRange`), so the live pass is both the most current source and
 * the SAME one the highlights are painted from — the rail cannot drift from the
 * document it is describing.
 *
 * ── the three sources, in precedence order ────────────────────────────────
 *  1. LIVE — the surface re-anchored this note this paint. Authoritative, and
 *     the reason a MOVED note sorts where it is NOW rather than where it was.
 *  2. RECORDED — the wire anchor (`block_ref` + `start`), used ONLY when no
 *     surface covered the note at all (the research pane is markdown and has no
 *     docz surface; an editing pane publishes no resolution; the first frame
 *     before the paint effect runs). Same coordinate space, one paint stale.
 *  3. NONE — the live pass found the note and classified it `orphan`, or the
 *     `block_ref` is the canvas editor's transport-id / `::`-address grammar
 *     which names no top-level block. These notes have NO position and MUST NOT
 *     be given a fake one; they go to the rail's named bucket.
 *
 * A note is never mixed between sources 1 and 2 WITHIN one block — coverage is
 * per-surface — which matters because the live `offset` counts characters and
 * the recorded one counts UTF-8 bytes. Both are monotone in position, so each is
 * a correct ordering inside its own block; they are simply never compared.
 */

/** A position on ONE read surface: which top-level block, and how far in. */
export interface SurfacePoint {
  readonly blockOrd: number;
  readonly offset: number;
}

/** A position in the REVIEW's reading order: surface, then block, then offset. */
export interface AnchorPoint extends SurfacePoint {
  readonly surfaceOrd: number;
}

/** Comment id → its live position on the surface that resolved it. */
export type SurfacePointMap = Readonly<Record<number, SurfacePoint>>;
/** Comment id → its position in the review's reading order. */
export type AnchorPointMap = Readonly<Record<number, AnchorPoint>>;

/** One read surface's live resolution. `points: null` = it has not resolved
 *  (not mounted, in edit mode, or the pane is hidden) — NOT "nothing anchored". */
export interface ReviewSurface {
  readonly kind: string;
  readonly points: SurfacePointMap | null;
}

/**
 * What the rail/gutter knows about where its notes live. `states` is the SAME
 * `anchorStates` map the cards already read, and it is load-bearing here: it is
 * the only thing that distinguishes "the live pass saw this note and its quote
 * is GONE" (→ no position) from "no surface covered this note" (→ fall back to
 * the recorded anchor).
 */
export interface DocumentOrder {
  readonly points: AnchorPointMap;
  readonly states: Readonly<Record<number, AnchorState>>;
}

/** The minimum a note must expose to be placed. `home_resource` is present on
 *  `WorkbenchComment` (the two-pane gutter) and absent on a plain `Comment`. */
export interface PlaceableComment {
  readonly id: number;
  readonly block_ref: string;
  readonly start: number;
  readonly home_resource?: string;
}

/** The pane's rank in the workbench's canonical left→right order. An unknown or
 *  absent surface ranks first — a single-surface rail has exactly one. */
export function surfaceRank(kind: string | undefined | null): number {
  if (!kind) return 0;
  const i = KIND_ORDER.indexOf(kind as (typeof KIND_ORDER)[number]);
  return i >= 0 ? i : 0;
}

/** Stamp each surface's live points with that surface's rank and fold them into
 *  one map. A surface that has not resolved (`points: null`) contributes
 *  nothing — its notes fall through to their recorded anchor. */
export function mergeSurfaces(surfaces: readonly ReviewSurface[]): AnchorPointMap {
  const out: Record<number, AnchorPoint> = {};
  for (const s of surfaces) {
    if (!s.points) continue;
    const rank = surfaceRank(s.kind);
    for (const [id, p] of Object.entries(s.points)) {
      out[Number(id)] = { surfaceOrd: rank, blockOrd: p.blockOrd, offset: p.offset };
    }
  }
  return out;
}

/** The `b{N}` INDEX grammar only. The canvas editor's transport ids and
 *  `::`-sub-block addresses (`T::td:3:1`) name no top-level block, so they yield
 *  NO recorded position rather than a guessed one. */
const B_INDEX = /^b(\d+)$/;

/** The position the note was WRITTEN at, from the wire. `null` when the
 *  `block_ref` is not the `b{N}` grammar. */
export function recordedPointOf(c: PlaceableComment): AnchorPoint | null {
  const m = B_INDEX.exec(c.block_ref ?? "");
  if (!m?.[1]) return null;
  const blockOrd = Number(m[1]);
  if (!Number.isFinite(blockOrd)) return null;
  return {
    surfaceOrd: surfaceRank(c.home_resource),
    blockOrd,
    offset: Math.max(0, Number(c.start) || 0),
  };
}

/**
 * Where this note sits in the document, or `null` when that genuinely cannot be
 * derived. See the module header for the precedence and why each step exists.
 */
export function documentPointOf(
  c: PlaceableComment,
  order: DocumentOrder | null | undefined,
): AnchorPoint | null {
  const live = order?.points[c.id];
  if (live) return live;
  // The live pass SAW this note and its quote is gone. A stale recorded anchor
  // would put it at a position the document no longer has — the bucket is the
  // honest answer.
  if (order?.states[c.id] === "orphan") return null;
  return recordedPointOf(c);
}

/** Reading order: surface, then block, then offset within the block. */
export function compareAnchorPoints(a: AnchorPoint, b: AnchorPoint): number {
  return (
    a.surfaceOrd - b.surfaceOrd || a.blockOrd - b.blockOrd || a.offset - b.offset
  );
}

/** The roots with NO derivable document position — the rail's named bucket.
 *  Replies are never included: only a root carries an anchor. */
export function unanchoredRootIds(
  roots: readonly ThreadNode[],
  order: DocumentOrder | null | undefined,
): ReadonlySet<number> {
  const ids = new Set<number>();
  for (const r of roots) {
    if (documentPointOf(r.comment, order) === null) ids.add(r.comment.id);
  }
  return ids;
}
